/**
 * Human Resources · records registry.
 *
 * Every entry in every staff file in one list — leave, reviews, discipline,
 * letters, documents, notes — with what is waiting for a decision on top.
 * Filter by kind, status, department or name; a row opens the person's file
 * at the entry. Reviews and discipline appear only for those who may read them.
 */
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useHrDecide, useHrRecords } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Field, Input, Segmented, Select, Skeleton, Table } from "../../design/components";
import {
  LEAVE_BY,
  leaveDays,
  leaveType,
  LETTER_BY,
  type HrRecord,
  type RecordKind,
  type RecordStatus,
} from "../../domain/hrFile";
import { REQUEST_BY } from "../../domain/hrClock";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useHrAccess } from "./access";
import { Avatar, fileLink, h, niceDate, PreviewBar, StatusChip, STATUS_WORD } from "./kit";
import { HrError, s, SampleBanner, SUITE } from "./shared";
import { KIND_WORD, tabOfKind } from "./StaffFilePage";

const KINDS: { value: "" | RecordKind; label: string }[] = [
  { value: "", label: "Everything" },
  { value: "leave", label: "Leave" },
  { value: "letter", label: "Letters" },
  { value: "request", label: "Requests" },
  { value: "review", label: "Reviews" },
  { value: "discipline", label: "Discipline" },
  { value: "document", label: "Documents" },
  { value: "note", label: "Notes" },
];

function detail(r: HrRecord): string {
  if (r.kind === "leave")
    return `${t(LEAVE_BY.get(leaveType(r))?.label ?? "")} · ${t("{n} days", { n: leaveDays(r) })} · ${niceDate(r.startsOn)} – ${niceDate(r.endsOn)}`;
  if (r.kind === "letter")
    return `${t(LETTER_BY.get(String(r.data.letter_type ?? ""))?.label ?? "")}${r.reference ? ` · ${r.reference}` : ""}`;
  if (r.kind === "review") return t(String(r.data.rating ?? ""));
  if (r.kind === "discipline") return t(String(r.data.action ?? ""));
  if (r.kind === "document") return String(r.data.file ?? "");
  if (r.kind === "request")
    return `${t(REQUEST_BY.get(String(r.data.type ?? ""))?.label ?? "")}${r.data.details ? ` · ${String(r.data.details)}` : ""}`;
  return String(r.data.text ?? "");
}

export function HrRecordsPage() {
  const session = useSession();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can } = useHrAccess();
  const q = useHrRecords();
  const decide = useHrDecide();
  const kind = (params.get("kind") ?? "") as "" | RecordKind;
  const status = (params.get("status") ?? "") as "" | RecordStatus;
  const [dept, setDept] = useState("");
  const [text, setText] = useState("");
  const all = useMemo(
    () => (q.data?.records ?? []).filter((r) => can.confidential || !r.confidential),
    [q.data, can.confidential],
  );
  const kinds = KINDS.filter((k) => can.confidential || (k.value !== "review" && k.value !== "discipline"));
  const departments = useMemo(
    () => [...new Set(all.map((r) => r.department).filter(Boolean))].sort() as string[],
    [all],
  );
  const rows = all.filter(
    (r) =>
      (!kind || r.kind === kind) &&
      (!status || r.status === status) &&
      (!dept || r.department === dept) &&
      (!text.trim() ||
        `${r.employeeName} ${r.employeeNo} ${r.title} ${r.reference ?? ""}`
          .toLowerCase()
          .includes(text.trim().toLowerCase())),
  );
  const waiting = all.filter((r) => r.status === "requested");
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  useScreenContext(
    q.data
      ? {
          route: "/hr-records",
          title: "Records registry",
          summary: [
            t("{n} records in the staff files; {w} waiting for a decision.", { n: all.length, w: waiting.length }),
          ],
          offers: [{ label: t("What is waiting for approval?"), ask: "which HR requests are waiting for approval?" }],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Records registry">
      <PreviewBar />
      <SampleBanner />
      {q.isLoading && <Skeleton rows={10} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {q.data && (
        <>
          {waiting.length > 0 && (
            <Card
              title={t("Waiting for a decision ({n})", { n: waiting.length })}
              sub={
                can.approve
                  ? t("Approve or reject here, or open the file first.")
                  : t("Deciding needs the approval permission.")
              }
            >
              <ul className={h.list}>
                {waiting.map((r) => (
                  <li key={r.id} className={h.item} data-testid="hr-waiting">
                    <Avatar name={r.employeeName} size={32} />
                    <span
                      className={`${h.itemMain} ${h.itemClick}`}
                      onClick={() => nav(fileLink(r.employeeId, tabOfKind(r.kind)))}
                    >
                      <b>
                        {r.employeeName} — {t(r.title)}
                      </b>
                      <span>{detail(r)}</span>
                    </span>
                    {can.approve && (
                      <span className={h.statusBtns}>
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={decide.isPending}
                          onClick={() =>
                            decide.mutate({ id: r.id, status: r.kind === "letter" ? "issued" : "approved" })
                          }
                          data-testid="hr-reg-approve"
                        >
                          {r.kind === "letter" ? t("Issue") : t("Approve")}
                        </Button>
                        <Button
                          size="sm"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: r.id, status: "rejected" })}
                        >
                          {t("Reject")}
                        </Button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Card title={t("Every record")} sub={t("Click a row to open the person's file at that record.")}>
            <div className={s.filters}>
              <Segmented
                value={kind}
                options={kinds}
                onChange={(v) => setParam("kind", v)}
                ariaLabel="Kind of record"
              />
              <Field label="Status">
                <Select value={status} onChange={(e) => setParam("status", e.target.value)}>
                  <option value="">{t("Any")}</option>
                  {(Object.keys(STATUS_WORD) as RecordStatus[]).map((st) => (
                    <option key={st} value={st}>
                      {t(STATUS_WORD[st])}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Department">
                <Select value={dept} onChange={(e) => setDept(e.target.value)}>
                  <option value="">{t("All departments")}</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Search">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t("Name, number, title or reference")}
                />
              </Field>
            </div>
            {rows.length === 0 ? (
              <EmptyState>{t("No record matches.")}</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>{t("Date")}</th>
                    <th>{t("Person")}</th>
                    <th>{t("Kind")}</th>
                    <th>{t("Title")}</th>
                    <th>{t("Details")}</th>
                    <th>{t("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 500).map((r) => (
                    <tr
                      key={r.id}
                      className={s.linkBtn}
                      style={{ cursor: "pointer" }}
                      onClick={() => nav(fileLink(r.employeeId, tabOfKind(r.kind)))}
                      data-testid="hr-reg-row"
                    >
                      <td className={s.mono}>{niceDate(r.occurredOn)}</td>
                      <td>
                        {r.employeeName} <span className={s.dim}>{r.employeeNo}</span>
                      </td>
                      <td>{t(KIND_WORD[r.kind])}</td>
                      <td>{t(r.title)}</td>
                      <td>{detail(r)}</td>
                      <td>
                        <StatusChip status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {rows.length > 500 && (
              <p className={s.note}>{t("The first 500 are shown; narrow the filters to see the rest.")}</p>
            )}
          </Card>
          {q.data.confidentialHidden && (
            <p className={s.note}>
              {t("Reviews and disciplinary actions need the confidential permission.")}
            </p>
          )}
        </>
      )}
    </Page>
  );
}
